/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { call, delay, put, race } from "redux-saga/effects";

import * as BenefitConstants from "../../Constants/BenefitConstants";
import * as benefitApi from "../../Services/BenefitApi";

import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import { IBenefitsDataResult } from "Interfaces/Benefits/IBenefitsDataResult";
import { IBenefitsRequest } from "Interfaces/Benefits/IBenefitsRequest";
import { IBenefitsResult } from "Interfaces/Benefits/IBenefitsResult";
import { IFilterValue } from "Interfaces/IFilterValue";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import { IApiErrorResult, IApiResult2 } from "../../Interfaces/IApiResult";
import { searchBenefitsFail, searchBenefitsSuccess, ISearchBenefits } from "./BenefitAction";

/**
 * Search all benefits for given text
 * @param {ISearchBenefits} action for searching benefits
 * @returns {void}
 */
function* handleSearchBenefits(action: ISearchBenefits) {
  const failedMessage = "Failed to search the benefits.";
  const requestBody = ({
    benefitName: action.benefitName,
    searchKey: action.searchText,
    pageIndex: action.searchPage,
    pageSize: action.pageSize,
    benefitStatusList: [],
    claimedFrom: undefined,
    claimedTo: undefined,
    uploadedFrom: undefined,
    uploadedTo: undefined,
    expirationStartDate: undefined,
    expirationEndDate: undefined,
    countryCodePool: undefined,
    sku: undefined,
    levelStatusName: undefined,
  } as unknown) as IBenefitsRequest;

  /**
   * Handle default filter request for O365/MS365/Teams Benefits.
   * Country-Code-Pool filter should have value (O365/MS365/Teams) by default
   */
  if (["M365", "Teams", "MS365"].includes(action.benefitName)) {
    const findCountryCodeIndex = action.selectedBenefitsFilterValues.findIndex(
      (x: IFilterValue) => x.fieldName == BenefitConstants.COUNTRY_CODE_POOL
    );
    if (findCountryCodeIndex < 0) {
      requestBody.countryCodePool =
        action.benefitName == BenefitConstants.BenefitNameEnum.M365
          ? BenefitConstants.BenefitNameEnum.O365
          : action.benefitName;
    }
  }

  action.selectedBenefitsFilterValues.forEach(function (item) {
    switch (item.fieldName) {
      case BenefitConstants.BENEFIT_STATUS:
        requestBody.benefitStatusList.push(item.value);
        break;
      case BenefitConstants.CLAIMED_FROM:
        requestBody.claimedFrom = new Date(item.value);
        break;
      case BenefitConstants.CLAIMED_TO:
        requestBody.claimedTo = new Date(item.value);
        break;
      case BenefitConstants.UPLOADED_FROM:
        requestBody.uploadedFrom = new Date(item.value);
        break;
      case BenefitConstants.UPLOADED_TO:
        requestBody.uploadedTo = new Date(item.value);
        break;
      case BenefitConstants.EXPIRATION_START_DATE:
        requestBody.expirationStartDate = new Date(item.value);
        break;
      case BenefitConstants.EXPIRATION_END_DATE:
        requestBody.expirationEndDate = new Date(item.value);
        break;
      case BenefitConstants.COUNTRY_CODE_POOL:
        requestBody.countryCodePool = item.value;
        break;
      case BenefitConstants.SKU:
        requestBody.sku = item.value;
        break;
      case BenefitConstants.SWAG_KIT:
        requestBody.levelStatusName = item.value;
        break;
    }
  });

  try {
    const { result, timeout }: IApiResult2<IBenefitsDataResult> | any = yield race({
      result: call(benefitApi.searchBenefits, requestBody, action.tenant),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchBenefitsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody as IBenefitsResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          searchBenefitsSuccess(
            parsedBody.benefitKeys,
            action.searchPage,
            action.pageSize,
            parsedBody.filteredCount,
            parsedBody.unclaimedCount
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(searchBenefitsFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(searchBenefitsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(searchBenefitsFail(error));
  }
}

export { handleSearchBenefits };
